import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Lightbulb, Target, ArrowDown } from 'lucide-react';
import { DjayAvatar } from '../../../mascot';
import { SummaryShell } from './SummaryShell';
/**
 * ReflectionCard — for sessions <30% accuracy (§19.5 + §20.2 G1).
 *
 * Three CTA-cards in priority order, with fallback so there are
 * always at least two meaningful choices besides "Klaar".
 */
interface ReflectionCardProps {
  djayTip: string;
  topErrorFamily?: string;
  onReviewErrors: () => void;
  onDjayExplain?: () => void;
  onLabSprint?: () => void;
  onEasierTier?: () => void;
  onExit: () => void;
}
export function ReflectionCard({
  djayTip,
  onReviewErrors,
  onDjayExplain,
  onLabSprint,
  onEasierTier,
  onExit
}: ReflectionCardProps) {
  // Build the card list with fallbacks. Review is always present.
  const ctas: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    sublabel?: string;
    onClick: () => void;
    emphasis?: boolean;
  }> = [{
    key: 'review',
    icon: <BookOpen className="h-5 w-5 text-indigo-500" data-id="element-10356" />,
    label: 'Bekijk wat er fout ging',
    sublabel: 'Zelf nakijken',
    onClick: onReviewErrors
  }];
  if (onDjayExplain) {
    ctas.push({
      key: 'explain',
      icon: <Lightbulb className="h-5 w-5 text-amber-500" data-id="element-10357" />,
      label: 'Djay legt het uit',
      sublabel: '1 minuutje',
      onClick: onDjayExplain
    });
  }
  if (onLabSprint) {
    ctas.push({
      key: 'sprint',
      icon: <Target className="h-5 w-5 text-emerald-500" data-id="element-10358" />,
      label: "Pak 'm in 2 min",
      sublabel: 'Korte sprint',
      onClick: onLabSprint,
      emphasis: true
    });
  } else if (onEasierTier) {
    ctas.push({
      key: 'easier',
      icon: <ArrowDown className="h-5 w-5 text-sky-500" data-id="element-10359" />,
      label: 'Probeer makkelijkere vragen',
      sublabel: 'Stapje terug',
      onClick: onEasierTier
    });
  }
  return <SummaryShell tone="reflection" data-id="element-10360">
      <div className="flex w-full max-w-md flex-col items-center" data-id="element-10361">
        {/* Djay + supportive line */}
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.35
      }} className="mb-6 flex flex-col items-center text-center" data-id="element-10362">
          <DjayAvatar emotion="supportive" size="lg" className="mb-3" data-id="element-10363" />
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-10364">
            Pittig setje vandaag
          </h2>
          <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-slate-600" data-id="element-10365">
            {djayTip}
          </p>
        </motion.div>

        {/* CTAs */}
        <div className="flex w-full flex-col gap-2.5" data-id="element-10366">
          {ctas.map((cta, i) => <motion.button key={cta.key} type="button" onClick={cta.onClick} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3 + i * 0.08,
          duration: 0.3
        }} whileHover={{
          y: -2
        }} whileTap={{
          scale: 0.98
        }} autoFocus={cta.emphasis || i === 0} className={`flex w-full items-center gap-3 rounded-2xl border bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md ${cta.emphasis ? 'border-emerald-200 ring-2 ring-emerald-100' : 'border-slate-100'}`} data-id="element-10367">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50" data-id="element-10368">
                {cta.icon}
              </span>
              <span className="min-w-0 flex-1" data-id="element-10369">
                <span className="block font-semibold text-slate-800" data-id="element-10370">
                  {cta.label}
                </span>
                {cta.sublabel && <span className="block text-xs text-slate-500" data-id="element-10371">
                    {cta.sublabel}
                  </span>}
              </span>
              <span className="text-slate-300" aria-hidden data-id="element-10372">
                →
              </span>
            </motion.button>)}
        </div>

        {/* Tertiary exit */}
        <motion.button type="button" onClick={onExit} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} className="mt-5 py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700" data-id="element-10373">
          Liever morgen
        </motion.button>
      </div>
    </SummaryShell>;
}